<template>
  <div>
    <div :class="dotsClass">...</div>
    <b-link v-if=hasLink ref="rendererSlotLink" :class="rendClass" data-test-path="hasData" @click="goTo">
    </b-link>
    <div v-else ref="rendererSlot" :class="rendClass" data-test-path="hasData"></div>
  </div>
</template>

<script>

import ImageViewer from "@/components/img/ImageViewer";
import BudgetingPlannerPM from "@/server/com/silkplm/model/plm/BudgetingPlannerPM";
import DynamicEntity from "@/components/forms/DynamicEntity";
import ActivityManager from "@/activity/ActivityManager";
import _ from "lodash";
import Vue from "vue";
import FeatureFlag from "@/server/com/silkplm/rpc/FeatureFlag";
import Product from "@/server/com/silkplm/model/plm/Product";
import OrderDetails from "@/server/com/silkplm/model/plm/OrderDetails";
import ProductInSeason from "@/server/com/silkplm/model/plm/ProductInSeason";
import FormsActions from "@/components/forms/FormsActions";


let counter = 0;

export default {
  name: "LoadingCellRenderer",

  created() {
    this.instanceId = counter++;
    let factory = this.rendererFactoryClass();
    this.renderer = new factory({
      propsData: {
        rowIndex: this.rowIndex,
        prop: this.prop,
        model: this.model,
        column: this.column,
        data: this.data,
        isPending: this.isPending
      }
    });

    this.$children.push(this.renderer);
    this.renderer.$parent = this;
    this.renderer.$root = this.$root;
    this.rowUuid = "";
  },

  beforeMount() {
    try {
      this.renderer.$mount?.();
    } catch (e) {
      console.error(e);
    }
  },

  mounted() {
    if (this.hasLink) {
      this.$refs.rendererSlotLink.$el.appendChild(this.renderer.$el);
    } else {
      this.$refs.rendererSlot.appendChild(this.renderer.$el);
    }
    this.renderer.$props.rowIndex = this.rowIndex;

    this.isPending = this.model?.__pending__;
    if (this.isPending) {
      this.dotsClass = "";
      this.rendClass = "d-none";
      setTimeout(() => {
        this.model?.__dataStream__?.requestRow(this.rowIndex, this.getRowUuid(), this);
      });
    } else {
      this.dotsClass = "d-none";
      this.rendClass = "";
    }
  },

  beforeDestroy() {
    let input = this.renderer;
    input.beforeUnmount?.();
    input.$destroy?.();
    const index = this.$children.indexOf(input);
    if (index > -1) {
      this.$children.splice(index, 1);
    }
    setTimeout(() => {
      try {
        input?.afterUnmount?.();
      } finally {
        setTimeout(() => {
          input.onDestroy?.();
        });
      }
    });
    this.input = null;
  },

  methods: {

    initializeLinkCache() {
      const idx = this.rowIndex;
      const row = this.data[idx];
      const uuid = this.getRowUuid();

      if (row && row.__computed_links__ && !this.linkCache[uuid]) {
        const clone = _.cloneDeep(row.__computed_links__);
        this.$set(this.linkCache, this.data[idx].id, clone);
        this.data[idx].__computed_links__ = clone;
      }
    },

    getLinkForRow() {
      const idx = this.rowIndex;
      const rowId = this.data[this.rowIndex].id;

      const fieldConfig = this.column.fieldConfig;
      const propName = fieldConfig.propNameToNavigateGridLink;
      const classNav = fieldConfig.gridLinkClassNavigator;
      const isView = this.column.formConfig.isView;

      let computed = this.linkCache[rowId];
      if (!computed) {
        computed = _.cloneDeep(this.data[idx].__computed_links__);
        this.$set(this.linkCache, rowId, computed);
      }
      this.data[idx].__computed_links__ = computed;

      // select by propName +  view/form
      let link;
      if (propName) {
        link = isView
            ? computed[classNav]?.[propName]
            : computed[propName];
      } else {
        link = computed[this.prop];
      }

      if (!link || !link.clazz) {
        const key = propName || this.prop;
        link = isView
            ? (computed[classNav]?.[key] || computed[key])
            : computed[key];
      }
      return link;
    },

    async goTo() {
      let link = this.getLinkForRow();
      if (!link) {
        let clazz = null;
        if (this.column.formConfig.isView) {
          clazz = this.column.formConfig.masterClass;
        } else {
          clazz = this.column.formConfig.formClassLong
        }
        let links = await Rpc.FormsStreams.computeLinksForBusinessRecord(this.data[this.rowIndex].id, clazz);
        this.data[this.rowIndex].__computed_links__[clazz] = links;
        link = this.getLinkForRow();
      }
      if (!link) {
        console.warn("No link result for row:", this.data[this.rowIndex]);
        return;
      }

      // specjalny override dla Product → ProductInSeason
      if (link.clazz === Product.getRemoteClass()
          && !SessionManager.hasFlag(FeatureFlag.SEPARATE_PRODUCT)
          && this.column.formConfig.formClassLong === OrderDetails.getRemoteClass()) {
        link.id = this.model.productInSeasonId;
        link.clazz = ProductInSeason.getRemoteClass();
      }

      await FormsActions.goTo(link);
    },

    forceRequestData() {
      if (!this.data || !this.data[this.rowIndex] || !this.data[this.rowIndex].__pending__ || !this.isPending) {
        return;
      }

      if (this.isPending && !this.data[this.rowIndex].__pending__) {
        this.isPending = false;
        this.dotsClass = "d-none";
        this.rendClass = "";
        this.renderer.$props.model = this.data[this.rowIndex];
        if (this.prop === '__meta__') {
          console.log(`Clear at forceRQ for meta`);
        }

        return;
      }
      if (this.prop === '__meta__') {
        console.log(`Clear2 at forceRQ for meta`);
      }
      this.dotsClass = "";
      this.rendClass = "d-none";
      this.model?.__dataStream__?.requestRow(this.rowIndex, this.getRowUuid(), this);
      this.isPending = true;
    },
    rendererFactoryClass: function () {
      return null;
    },
    getRowUuid() {
      if (this.rowUid) {
        return this.rowUid;
      }

      if (this.rowUuid) {
        return this.rowUuid;
      }
      let el = this.$el;
      while (el) {
        if (el.hasAttribute('data-row-id')) {
          return el.getAttribute('data-row-id');
        }

        if (el.tagName.toUpperCase() === 'REVOGR-DATA') {
          this.setupRowsNumbers(el);
          return this.getRowUuid();
        }

        el = el.parentNode;
      }
      if (!el) {
        console.error("No ROW Uuid found");
      }
      return this.rowUuid;
    },
    setupRowsNumbers(parentElement) {
      for (let i = 0; i < parentElement.children.length; i++) {
        const childElement = parentElement.children[i];
        childElement.setAttribute('data-row-id', `r-${i}`);
      }
    },
    refresh() {

    }
  },

  watch: {
    model: function (val) {
      this.isPending = val?.__pending__;

      if (this.isPending) {
        this.dotsClass = "";
        this.rendClass = "d-none";

      } else {
        //console.log(val.__row_conf);
        this.dotsClass = "d-none";
        this.rendClass = "";
        this.renderer.$props.model = val;
        this.initializeLinkCache();
      }
    },
    data: function (val) {

      //console.log(this.model?.__pending__);
      this.renderer.$props.data = val;
      this.isPending = val[this.rowIndex]?.__pending__;
      if (this.isPending) {
        this.dotsClass = "";
        this.rendClass = "d-none";


      } else {
        //console.log(val.__row_conf);
        this.dotsClass = "d-none";
        this.rendClass = "";
        this.renderer.$props.model = val[this.rowIndex];
        this.initializeLinkCache();
      }
    },
    prop: function (val) {
      this.renderer.$props.prop = val;
    },
    column: function (val) {
      this.renderer.$props.column = val;
    },
    rowIndex: function (val) {
      this.renderer.$props.rowIndex = val;
      this.isPending = this.data[this.rowIndex]?.__pending__;
      if (this.isPending) {
        this.dotsClass = "";
        this.rendClass = "d-none";
        // console.log("request row in row Index change ");
        this.model?.__dataStream__?.requestRow(val, this.getRowUuid(), this);
      } else {
        //console.log(val.__row_conf);
        this.dotsClass = "d-none";
        this.rendClass = "";
        this.renderer.$props.model = this.data[this.rowIndex];
      }

    },
  },
  data: function () {
    return {
      isPending: false,
      dotsClass: '',
      rendClass: 'd-none',
      linkCache: {}
    }
  },
  props: ["rowIndex", "prop", "model", "column", "data", "rowUid", "hasLink"]

};
</script>